import React, { createContext, ReactNode, useCallback, useMemo, useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { PublicNode, SearchNodeTerm } from '../../__generated__/api'
import api from '../services/api'
import { flattenNodes, flattenTerms } from '../utils/nodes'

import { useApi } from './useApi'
import { useOHC } from './useOHC'

const filterNodesRecursive = (
  nodes: PublicNode[],
  predicate: (node: PublicNode) => boolean,
  sortFn?: (a: PublicNode, b: PublicNode) => number
): PublicNode[] => {
  const filtered = nodes.filter(predicate).map((node) => {
    const filteredChildren = node.children ? filterNodesRecursive(node.children, predicate) : []
    return {
      ...node,
      children: filteredChildren.length > 0 ? filteredChildren : undefined,
    }
  })
  return sortFn ? filtered.sort(sortFn) : filtered
}

type NodesContextType = {
  nodes: PublicNode[]
  listedNodes: PublicNode[]
  favoriteNodes: PublicNode[]
  flattenedNodes: PublicNode[]
  getNodeById(id?: string): { node: PublicNode | null; pending: boolean }
  isNodeListed(id?: string): boolean
  getTerm(id?: string, term?: string, type?: string | string[]): SearchNodeTerm | null
}

const NodesContext = createContext<NodesContextType>({
  nodes: [],
  listedNodes: [],
  favoriteNodes: [],
  flattenedNodes: [],
  getNodeById: () => ({ node: null, pending: true }),
  isNodeListed: () => false,
  getTerm: () => null,
})

export const NodesProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const { i18n } = useTranslation()
  const { isOHCSide, ohcAllowedStatus } = useOHC()
  const shouldMakeRequest = isOHCSide ? ohcAllowedStatus === 'allowed' : true
  const getOhcNodes = isOHCSide && ohcAllowedStatus === 'allowed'

  const { data: allNodes, pending: allNodesPending } = useApi(
    getOhcNodes ? api.v2.allOhcNodes : api.v2.allPrivateNodes,
    { lang: i18n.language },
    [],
    shouldMakeRequest
  )

  const flattenedNodes = useMemo(() => flattenNodes(allNodes), [allNodes])
  const flattenedTerms = useMemo(() => flattenTerms(allNodes), [allNodes])

  const favoriteNodes = useMemo(() => {
    if (!allNodes) return []
    const flattened = flattenNodes(allNodes)
    return flattened
      .filter((n) => n.isFavorite === true)
      .sort((a, b) => (a.favoriteOrder ?? Infinity) - (b.favoriteOrder ?? Infinity))
      .map((n) => {
        return { ...n, children: n.children?.filter((c) => c.isListed === true) }
      })
  }, [allNodes])

  const listedNodes = useMemo(
    () => (allNodes ? filterNodesRecursive(allNodes, (node) => node.isListed === true) : []),
    [allNodes]
  )

  const flattenedListedNodes = useMemo(() => flattenNodes(listedNodes), [listedNodes])

  const getNodeById = useCallback(
    (id?: string) => {
      if (!id) {
        return { node: null, pending: false }
      }

      return {
        node: flattenedNodes.find((node) => node.id === id) ?? null,
        pending: allNodesPending,
      }
    },
    [flattenedNodes, allNodesPending]
  )

  const getTerm = useCallback(
    (id?: string, term?: string, type?: string | string[]) => {
      if (!id && !term && !type) {
        return null
      }

      return (
        flattenedTerms.find(
          (t) =>
            t.id === id ||
            (t.term === term && Array.isArray(type) ? type.includes(t.type) : t.type === type)
        ) ?? null
      )
    },
    [flattenedTerms]
  )

  const isNodeListed = useCallback(
    (id?: string) => {
      if (!id) {
        return false
      }

      return flattenedListedNodes.some((node) => node.id === id)
    },
    [flattenedListedNodes]
  )

  const memoized = useMemo(
    () => ({
      nodes: allNodes,
      listedNodes,
      favoriteNodes,
      flattenedNodes,
      getNodeById,
      isNodeListed,
      getTerm,
    }),
    [allNodes, favoriteNodes, listedNodes, flattenedNodes, getNodeById, isNodeListed, getTerm]
  )

  return <NodesContext.Provider value={memoized}>{children}</NodesContext.Provider>
}

export const useNodes = () => {
  return useContext(NodesContext)
}

export const useNode = (
  nodeId: string | undefined
): { node: PublicNode | null; pending: boolean } => {
  const { getNodeById } = useNodes()

  return getNodeById(nodeId)
}

export const useTerm = (
  termId: string | undefined,
  term: string | undefined,
  type: string | string[] | undefined
): SearchNodeTerm | null => {
  const { getTerm } = useNodes()

  return getTerm(termId, term, type)
}
